// Account at /account, from Account.dc.html: the board's own header (the wordmark, Library and the
// member's initials; no search and no bell), "Your account" with the left nav, and the sections
// Profile, Logging in, My programmes, Referrals (decision 55, 13 Oct: no board draws it), Billing
// (only for someone with a Stripe customer) and Your data. The board's Emails section, with its 3 switches, is gone: the hub sends no notification
// emails (Adam, 5 Oct), so there's nothing to switch off, and what's new shows in the bell. The
// footer with the help address (decision 38) and the payment banner (stage 2, data-model 3.2) stay
// on it, as on every shell page, though the board draws neither.
//
// Phase 1 hides About you, the member directory switch and Change photo (Adam, 2 Oct). While staff
// view as a member, every control is read-only and the page says so with the shared line
// (lib/viewAs.jsx): the functions and the database refuse those writes too.
//
// No board draws Account on a phone. At phone width it takes PhoneLibrary.dc.html's header, gutter
// and card look, with the nav as a row that stays at the top of the page.
import { useCallback, useEffect, useRef, useState } from 'react'
import { setTitle } from '../config/site.js'
import { useMember } from '../app/member.jsx'
import { useLibraryModel } from '../library/useModel.js'
import { useNow } from '../lib/clock.js'
import { usePhone } from '../lib/media.js'
import { initials as initialsOf } from '../lib/format.js'
import { useViewingAs, ReadOnlyNote } from '../lib/viewAs.jsx'
import { Footer } from '../shell/Shell.jsx'
import { PaymentBanner } from '../shell/PaymentBanner.jsx'
import BillingSection from '../billing/BillingSection.jsx'
import { AccountHeader } from '../account/AccountHeader.jsx'
import { AccountNav } from '../account/AccountNav.jsx'
import Profile from '../account/Profile.jsx'
import LoggingIn from '../account/LoggingIn.jsx'
import Programmes from '../account/Programmes.jsx'
import YourData from '../account/YourData.jsx'
import Referrals from '../account/Referrals.jsx'
import { COPY } from '../account/copy.js'
import { loadAccount } from '../account/load.js'
import { programmeRows, hasCustomer, securedBalances } from '../account/programmes.js'
import '../styles/shell.css'
import '../styles/library.css'
import '../styles/account.css'

export default function Account() {
  const { me, lib, refreshLibrary, loadMe } = useMember()
  const model = useLibraryModel()
  const phone = usePhone()
  const nowMs = useNow()
  const personId = me.person.id
  const readOnly = useViewingAs()
  const [acct, setAcct] = useState({ status: 'loading' })
  const firstLibrary = useRef(refreshLibrary)

  useEffect(() => { setTitle(COPY.title) }, [])
  // the Library's rows, fresh on every visit, as the Library itself does: My programmes and
  // whether Billing shows come from them
  useEffect(() => { firstLibrary.current() }, [])

  const load = useCallback(() => {
    setAcct(a => (a.status === 'ready' ? a : { status: 'loading' }))
    return loadAccount(personId)
      .then(d => setAcct({ status: 'ready', ...d }))
      .catch(() => setAcct(a => (a.status === 'ready' ? a : { status: 'error' })))
  }, [personId])
  useEffect(() => { load() }, [load])

  const personNow = useRef(null)
  personNow.current = acct.status === 'ready' ? acct.person : null
  const setPerson = useCallback(row => {
    const before = personNow.current
    setAcct(a => (a.status === 'ready' ? { ...a, person: { ...a.person, ...row } } : a))
    // the header's initials and the Library's greeting follow a new name
    const renamed = before && row.full_name !== undefined && row.full_name !== before.full_name
    if (renamed) loadMe()
  }, [loadMe])
  // a function's 409 shared_login: the login is shared now, so the forms give way to the lines
  const setShared = useCallback(() => setAcct(a => (a.status === 'ready' ? { ...a, shared: true } : a)), [])
  const emailChanged = useCallback(() => { load().then(() => loadMe()) }, [load, loadMe])

  const billing = hasCustomer(lib.data)
  const sections = [
    { id: 'profile', label: COPY.profile },
    { id: 'login', label: COPY.loggingIn },
    { id: 'programmes', label: COPY.programmes },
    // Referrals (decision 55): for everyone, quiet unless Adam has ticked something for them
    { id: 'referrals', label: COPY.referrals.title },
    ...(billing ? [{ id: 'billing', label: 'Billing' }] : []),
    { id: 'data', label: COPY.yourData },
  ]

  // /account#billing (Stripe's way back), and a link to any other section, open at that section
  // once it's drawn: the router waits for it (lib/router.jsx, scrollToHash)
  const ready = acct.status === 'ready'
  const person = ready ? acct.person : null
  const initials = initialsOf(person?.full_name || me.person.full_name)
  // a secured place's pay button carries their email, as the place secured page's does
  const rows = ready ? programmeRows(lib.data, acct.terms, nowMs, acct.certificates, me.person.email) : null
  const shared = ready && acct.shared === true
  const secured = securedBalances(lib.data, nowMs, me.person.email)

  return (
    <div className="ac-page">
      <AccountHeader initials={initials} phone={phone} />
      {model && <PaymentBanner text={model.banner} target={model.bannerFor} phone={phone} />}
      <div className="ac-body">
        <h1 className="ac-h1">{COPY.title}</h1>
        <AccountNav sections={sections} phone={phone} label={COPY.navLabel} />
        <main className="ac-col">
          <ReadOnlyNote />
          {acct.status === 'loading' && <div className="ac-loading" aria-busy="true" />}
          {acct.status === 'error' && (
            <section className="ac-card" aria-labelledby="ac-failed-h">
              <h2 id="ac-failed-h" className="ac-h2">{COPY.loadFailedTitle}</h2>
              <p className="ac-lede">{COPY.loadFailedLine}</p>
              <div className="ac-row"><button type="button" className="ac-btn ac-btn--ink" onClick={load}>{COPY.tryAgain}</button></div>
            </section>
          )}
          {ready && (
            <>
              <Profile key={`profile-${person.id}`} person={person} shared={shared} emailChange={acct.emailChange} readOnly={readOnly}
                onPerson={setPerson} onEmailChanged={emailChanged} onShared={setShared} />
              <LoggingIn person={person} shared={shared} readOnly={readOnly} onPerson={setPerson} onShared={setShared} />
              <Programmes rows={rows} readOnly={readOnly} />
              <Referrals key={`referrals-${person.id}`} readOnly={readOnly} />
              {billing && <BillingSection readOnly={readOnly} secured={secured} />}
              <YourData personId={person.id} readOnly={readOnly} />
            </>
          )}
        </main>
      </div>
      <Footer phone={phone} />
    </div>
  )
}
